import React, { useEffect, useState } from "react";
import {
  fetchDailySentences,
  deleteDailySentence,
  deleteCategory,
  fetchCategories,
} from "./adminDailySentenceService";
import DailySentenceForm from "./DailySentenceForm";
import DailySentenceCategoryForm from "./DailySentenceCategoryForm";
import "../../blog/tongueTwister.css";

const AdminDailySentencePage = () => {
  const [tab, setTab] = useState("sentences");

  // Categories
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);

  // Sentences
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [level, setLevel] = useState("");

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { loadItems(); }, [page, search, categoryId, level]);

  const loadCategories = async () => {
    console.log("[DailySentencePage] Loading categories");
    setCatLoading(true);
    const res = await fetchCategories();
    if (res.status === 200) {
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      console.log("[DailySentencePage] Categories loaded:", list.length);
      setCategories(list);
    } else {
      console.error("[DailySentencePage] Categories load failed:", res.status, res.message);
    }
    setCatLoading(false);
  };

  const loadItems = async () => {
    console.log("[DailySentencePage] Loading sentences, page:", page, "categoryId:", categoryId, "level:", level, "search:", search);
    setLoading(true);
    const params = { page, size: 20 };
    if (search) params.search = search;
    if (categoryId) params.categoryId = categoryId;
    if (level) params.level = level;

    const res = await fetchDailySentences(params);
    if (res.status === 200 && res.data) {
      const list = res.data.content || [];
      console.log("[DailySentencePage] Loaded", list.length, "sentences, totalPages:", res.data.totalPages);
      setItems(list);
      setTotalPages(res.data.totalPages || 0);
    } else {
      console.error("[DailySentencePage] Load failed:", res.status, res.message);
      setItems([]);
      alert(res.message || "Failed to load");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this daily sentence?")) return;
    console.log("[DailySentencePage] Deleting id:", id);
    const res = await deleteDailySentence(id);
    if (res.status === 200 || res.status === 204) {
      console.log("[DailySentencePage] Delete successful");
      loadItems();
    } else {
      console.error("[DailySentencePage] Delete failed:", res.status, res.message);
      alert(res.message);
    }
  };

  const onSentenceSaved = () => {
    console.log("[DailySentencePage] Sentence saved, reloading");
    setShowForm(false);
    setEditing(null);
    loadItems();
    loadCategories();
  };

  const onCategorySaved = () => {
    console.log("[DailySentencePage] Category saved, reloading");
    setShowCatForm(false);
    setEditingCat(null);
    loadCategories();
  };

  const handleDeleteCategory = async (id, sentenceCount) => {
    if (sentenceCount > 0) {
      alert("Cannot delete category with existing sentences. Please delete all sentences first or move them to another category.");
      return;
    }
    if (!window.confirm("Delete this category?")) return;
    console.log("[DailySentencePage] Deleting category id:", id);
    const res = await deleteCategory(id);
    if (res.status === 200 || res.status === 204) {
      console.log("[DailySentencePage] Category delete successful");
      loadCategories();
    } else {
      console.error("[DailySentencePage] Category delete failed:", res.status, res.message);
      alert(res.message);
    }
  };

  const handleFilterChange = (setter) => (e) => {
    setPage(0);
    setter(e.target.value);
  };

  const tabStyle = (t) => ({
    padding: "8px 20px", cursor: "pointer", fontWeight: 600, fontSize: 14,
    border: "none", borderBottom: tab === t ? "3px solid #019095" : "3px solid transparent",
    background: "none", color: tab === t ? "#019095" : "#666",
  });

  return (
    <div className="admin-container">
      <h2>Daily Sentence Management</h2>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #eee", marginBottom: 20 }}>
        <button style={tabStyle("sentences")} onClick={() => { console.log("[DailySentencePage] Switched to Sentences tab"); setTab("sentences"); }}>Sentences</button>
        <button style={tabStyle("categories")} onClick={() => { console.log("[DailySentencePage] Switched to Categories tab"); setTab("categories"); }}>Categories</button>
      </div>

      {/* ===== CATEGORIES TAB ===== */}
      {tab === "categories" && (
        <>
          <div className="admin-header" style={{ marginBottom: 16 }}>
            <span />
            <button onClick={() => { console.log("[DailySentencePage] Opening new category form"); setEditingCat(null); setShowCatForm(true); }}>+ New Category</button>
          </div>

          {showCatForm && (
            <DailySentenceCategoryForm category={editingCat} onSaved={onCategorySaved} onCancel={() => setShowCatForm(false)} />
          )}

          {catLoading ? <p>Loading...</p> : (
            <table className="announcement-table">
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>Title</th>
                  <th>Key</th>
                  <th>Sentences</th>
                  <th>Order</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr><td colSpan="7">No categories found</td></tr>
                ) : (
                  categories.map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontSize: "1.4rem", textAlign: "center" }}>{c.icon || "—"}</td>
                      <td><strong>{c.title}</strong></td>
                      <td style={{ fontSize: 12, color: "#888" }}>{c.categoryName || c.category}</td>
                      <td style={{ textAlign: "center" }}>{c.sentenceCount || 0}</td>
                      <td style={{ textAlign: "center" }}>{c.sortOrder}</td>
                      <td style={{ textAlign: "center" }}>{c.isActive ? "✅" : "❌"}</td>
                      <td>
                        <button onClick={() => { console.log("[DailySentencePage] Editing category id:", c.id, c.title); setEditingCat(c); setShowCatForm(true); }}>Edit</button>
                        <button className="delete-btn" onClick={() => handleDeleteCategory(c.id, c.sentenceCount || 0)}>Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* ===== SENTENCES TAB ===== */}
      {tab === "sentences" && (
        <>
          <div className="admin-header" style={{ marginBottom: 16 }}>
            <span />
            <button onClick={() => { console.log("[DailySentencePage] Opening new sentence form"); setEditing(null); setShowForm(true); }}>+ New Sentence</button>
          </div>

          {showForm && (
            <DailySentenceForm sentence={editing} onSaved={onSentenceSaved} onCancel={() => setShowForm(false)} />
          )}

          {/* Filters */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <input
              type="text" placeholder="Search sentences..." value={search}
              onChange={handleFilterChange(setSearch)}
              style={{ flex: 1, minWidth: 200, padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd" }}
            />
            <select value={categoryId} onChange={handleFilterChange(setCategoryId)} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd" }}>
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.icon} {c.title} ({c.sentenceCount || 0})</option>
              ))}
            </select>
            <select value={level} onChange={handleFilterChange(setLevel)} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd" }}>
              <option value="">All Levels</option>
              <option value="EASY">Easy</option>
              <option value="MEDIUM">Medium</option>
              <option value="HARD">Hard</option>
            </select>
          </div>

          {loading ? <p>Loading...</p> : (
            <table className="announcement-table">
              <thead>
                <tr>
                  <th>Sentence</th>
                  <th>Category</th>
                  <th>Level</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan="4">No sentences found</td></tr>
                ) : (
                  items.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <strong>{s.title}</strong>
                        {s.description && <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{s.description}</div>}
                      </td>
                      <td>{s.categoryTitle || s.categoryDisplayName || s.categoryName || s.category}</td>
                      <td>
                        <span style={{
                          padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, color: "#fff",
                          background: s.level === "EASY" ? "#4caf50" : s.level === "MEDIUM" ? "#ff9800" : "#e91e63",
                        }}>
                          {s.levelDisplayName || s.level}
                        </span>
                      </td>
                      <td>
                        <button onClick={() => { console.log("[DailySentencePage] Editing sentence id:", s.id, s.title); setEditing(s); setShowForm(true); }}>Edit</button>
                        <button className="delete-btn" onClick={() => handleDelete(s.id)}>Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {totalPages > 1 && (
            <div style={{ marginTop: 20 }}>
              <button disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
              <span style={{ margin: "0 10px" }}>Page {page + 1} / {totalPages}</span>
              <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDailySentencePage;
